/**
 * Shows how close the customer is to the next bulk-pricing tier for the selected variant, counting what is already in
 * the cart. Wholesale minimums apply to the total quantity of a size across the whole order, not to a single design,
 * so a customer splitting 25 + 25 + 25 across three uploads still reaches the tier. The quantity input itself is not
 * constrained; this element only informs, and prefills a sensible starting quantity.
 *
 * Tier thresholds are read from the volume discount table the DealEasy app renders on the page. That table is
 * app-owned markup, so parsing is treated as best effort: when it yields nothing we fall back to the
 * "custom.min_quantity" variant metafield and show a single-threshold message, and when that is missing too we render
 * nothing rather than guess.
 *
 * This element wraps <quantity-selector>. Listeners on an ancestor always run after listeners on the descendant that
 * was the event target, so our handlers reliably run after the theme's own quantity selector handlers without having
 * to touch the theme bundle.
 */
(function () {
  var TIER_ROW_SELECTOR = '.lb-vd-table-body-row';
  var TIER_QUANTITY_SELECTOR = '[data-label="Quantity"]';
  var TIER_DISCOUNT_SELECTOR = '[data-label="Discount"]';
  var DISCOUNT_TABLE_SELECTOR = '#lb-volume-discount';

  // The discount table is rendered by the app after our own scripts run, so we watch for it. The watch is dropped
  // once found, and gives up after this long so we are not observing the whole document forever.
  var TABLE_WATCH_TIMEOUT = 15000;

  function toPositiveInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return parsed > 0 ? parsed : fallback;
  }

  function fill(template, values) {
    return Object.keys(values).reduce(function (text, key) {
      return text.split('[' + key + ']').join(values[key]);
    }, template || '');
  }

  var BulkPricingProgress = class extends HTMLElement {
    constructor() {
      super();

      this.render = this.render.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onCartChange = this.onCartChange.bind(this);
      this.onCartRefresh = this.onCartRefresh.bind(this);
      this.onDiscountTableAppeared = this.onDiscountTableAppeared.bind(this);
    }

    connectedCallback() {
      this.abortController = new AbortController();

      var options = { signal: this.abortController.signal };

      this.addEventListener('input', this.render, options);
      this.addEventListener('click', this.render, options);
      document.addEventListener('variant:change', this.onVariantChange, options);
      document.addEventListener('cart:change', this.onCartChange, options);
      document.addEventListener('cart:refresh', this.onCartRefresh, options);

      this.watchDiscountTable();
      this.render();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.tableObserver?.disconnect();
      clearTimeout(this.tableWatchTimer);
    }

    get input() {
      return this.querySelector('.quantity-selector__input');
    }

    get panel() {
      return this.querySelector('[data-bulk-pricing-progress]');
    }

    get quantity() {
      return toPositiveInt(this.input?.value, 0);
    }

    get variantId() {
      // The selected variant lives in the product form's hidden "id" input, which the theme keeps up to date.
      return this.selectedVariantId || this.input?.form?.querySelector('[name="id"]')?.value;
    }

    get pricingByVariant() {
      if (!this.parsedPricing) {
        var script = this.querySelector('script[data-variant-pricing]');

        try {
          this.parsedPricing = JSON.parse(script?.textContent || '{}');
        } catch (error) {
          this.parsedPricing = {};
        }
      }

      return this.parsedPricing;
    }

    pricingFor(variantId) {
      return this.pricingByVariant[String(variantId)] || { min: 1, inCart: 0, name: '' };
    }

    /**
     * Reads the tier thresholds out of the app's discount table. Rows read like "150 - 199" or "800+", so the first
     * number in the cell is the threshold.
     */
    scrapeTiers() {
      var rows = Array.from(document.querySelectorAll(TIER_ROW_SELECTOR));

      var tiers = rows
        .map(function (row) {
          var quantityText = row.querySelector(TIER_QUANTITY_SELECTOR)?.textContent || '';
          var discountText = row.querySelector(TIER_DISCOUNT_SELECTOR)?.textContent || '';
          var threshold = toPositiveInt((quantityText.match(/\d+/) || [])[0], 0);

          return threshold ? { quantity: threshold, label: discountText.trim() } : null;
        })
        .filter(Boolean)
        .sort(function (a, b) {
          return a.quantity - b.quantity;
        });

      // Guard against a half-rendered table by keeping the tiers we already had.
      return tiers.length ? tiers : null;
    }

    watchDiscountTable() {
      var table = document.querySelector(DISCOUNT_TABLE_SELECTOR);

      this.tableObserver?.disconnect();

      if (table) {
        // Our own output lives outside the table, so re-rendering on its mutations cannot feed back into itself.
        clearTimeout(this.tableWatchTimer);
        this.tableObserver = new MutationObserver(this.render);
        this.tableObserver.observe(table, { childList: true, subtree: true, characterData: true });
        return;
      }

      // The app has not rendered its table yet. Watch the document only for the table appearing: re-rendering on
      // every document mutation would loop, because rendering mutates the document too.
      this.tableObserver = new MutationObserver(this.onDiscountTableAppeared);
      this.tableObserver.observe(document.body, { childList: true, subtree: true });

      // Watching the whole document is only acceptable until the app has had a fair chance to render.
      this.tableWatchTimer = setTimeout(() => this.tableObserver?.disconnect(), TABLE_WATCH_TIMEOUT);
    }

    onDiscountTableAppeared() {
      if (document.querySelector(DISCOUNT_TABLE_SELECTOR)) {
        this.watchDiscountTable();
        this.render();
      }
    }

    onVariantChange(event) {
      var input = this.input,
        variant = event.detail?.variant,
        form = event.target;

      if (!input || !variant) {
        return;
      }

      // Ignore variant changes belonging to another product form on the page.
      if (input.form ? input.form !== form : !form.contains?.(this)) {
        return;
      }

      // The quantity the customer entered is deliberately left untouched; only the progress is recalculated.
      this.selectedVariantId = variant.id;
      this.render();
    }

    onCartChange(event) {
      var items = event.detail?.cart?.items;

      if (items) {
        this.applyCartQuantities(items);
      }
    }

    async onCartRefresh() {
      try {
        var cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        this.applyCartQuantities(cart.items || []);
      } catch (error) {
        // A failed refresh just leaves the previous totals in place.
      }
    }

    applyCartQuantities(items) {
      var pricingByVariant = this.pricingByVariant;

      Object.keys(pricingByVariant).forEach(function (variantId) {
        pricingByVariant[variantId].inCart = 0;
      });

      items.forEach(function (item) {
        var pricing = pricingByVariant[String(item.variant_id)];

        if (pricing) {
          pricing.inCart += item.quantity;
        }
      });

      this.render();
    }

    /**
     * Resolves the tiers to measure against: the app's table when we can read it, otherwise the variant's own
     * minimum as a single unlabelled threshold.
     */
    tiersFor(pricing) {
      this.tiers = this.scrapeTiers() || this.tiers;

      if (this.tiers) {
        return { tiers: this.tiers, isFallback: false };
      }

      var minimum = toPositiveInt(pricing.min, 1);

      return minimum > 1
        ? { tiers: [{ quantity: minimum, label: this.copy('fallback-discount') }], isFallback: true }
        : { tiers: [], isFallback: true };
    }

    copy(name) {
      return this.panel?.getAttribute('data-copy-' + name) || '';
    }

    render() {
      var panel = this.panel;

      if (!panel) {
        return;
      }

      var pricing = this.pricingFor(this.variantId),
        resolved = this.tiersFor(pricing),
        total = toPositiveInt(pricing.inCart, 0) + this.quantity;

      if (!resolved.tiers.length) {
        panel.hidden = true;
        return;
      }

      var reached = null,
        next = null;

      resolved.tiers.forEach(function (tier) {
        if (total >= tier.quantity) {
          reached = tier;
        } else if (!next) {
          next = tier;
        }
      });

      var target = next ? next.quantity : reached.quantity;

      panel.hidden = false;
      panel.classList.toggle('bulk-pricing-progress--unlocked', Boolean(reached));

      this.querySelector('[data-bulk-pricing-bar]').style.width =
        Math.min(100, Math.round((total / target) * 100)) + '%';

      this.querySelector('[data-bulk-pricing-message]').textContent = this.messageFor({
        reached: reached,
        next: next,
        total: total,
        name: pricing.name,
        isFallback: resolved.isFallback,
      });
    }

    messageFor(state) {
      if (!state.next) {
        return state.isFallback
          ? this.copy('fallback-unlocked')
          : fill(this.copy('unlocked-top'), { current: state.reached.label });
      }

      var values = {
        count: state.next.quantity - state.total,
        discount: state.next.label,
        variant: state.name,
        current: state.reached?.label,
      };

      return fill(this.copy(state.reached ? 'unlocked-next' : 'unlock'), values);
    }
  };

  if (!window.customElements.get('bulk-pricing-progress')) {
    window.customElements.define('bulk-pricing-progress', BulkPricingProgress);
  }
})();
